import { LockManager, Partial } from "@pdfking/tfunc"

type WasmSessionOptions = {
  init_timeout?: number
  dispose_timeout?: number
}

type Disposable = {
  init: () => Promise<void>
  dispose: () => Promise<void>
}

export const CreateWasmSession = <T extends Disposable>(
  factory: () => Promise<T>,
  options?: WasmSessionOptions
) => {
  let current_api: T | undefined
  let init_timer: ReturnType<typeof setTimeout> | undefined
  let dispose_timer: ReturnType<typeof setTimeout> | undefined
  let active_sessions = 0
  const { schedule } = LockManager()
  const INIT_MS = options?.init_timeout ?? 1000
  const DISPOSE_MS = options?.dispose_timeout ?? 5000

  const clear_timers = (onInit = true, onDispose = true) => {
    if (onInit && init_timer) {
      clearTimeout(init_timer)
      init_timer = undefined
    }
    if (onDispose && dispose_timer) {
      clearTimeout(dispose_timer)
      dispose_timer = undefined
    }
  }

  const locked = (func: () => Promise<void>, onInit = true, onDispose = true) =>
    schedule("λ", func).then(Partial(clear_timers, onInit, onDispose))

  return async function* <R>(fn: (api: T) => AsyncGenerator<R>): AsyncGenerator<R> {
    await locked(async () => {
      active_sessions++
      if (!current_api) current_api = await factory()
    })

    try {
      yield* fn(current_api!)
    } finally {
      active_sessions--
      clear_timers()
      if (active_sessions === 0) {
        init_timer = setTimeout(
          () =>
            locked(
              async () => {
                await current_api?.init()
              },
              true,
              false
            ),
          INIT_MS
        )
        dispose_timer = setTimeout(
          () =>
            locked(
              async () => {
                await current_api?.dispose()
                current_api = undefined
              },
              false,
              true
            ),
          DISPOSE_MS
        )
      }
    }
  }
}
